import { useRouter } from 'next/router'

import { Spinner } from '@wartek-id/spinner'
import { Text } from '@wartek-id/text-v1'

import { useApiGetPsProfile } from 'api/kinerja/kepala-sekolah/pengawas-sekolah/profil'
import { useAPIKinerjaProfile } from 'api/kinerja/useAPIKinerja'
import { useAPIGetPSPegawaiProfile } from 'api/kinerja/pengawas-sekolah/profile'
import { useAPIGetPenilikPegawaiProfile } from 'api/kinerja/penilik-pegawai/profile'
import { RoleContext } from 'app/KinerjaShared/hooks/useRoleContext'
import {
  isKsDefinitifPlt,
  isUserHasKsRole,
  isUserHasPltRole,
  isUserHasRole,
} from 'app/KinerjaKSPage/utils/profile'
import {
  isUserKepalaDinas,
  isUserPengawasSekolah,
  isUserKepalaDinasAndPengawasSekolah,
  isPSUserHasRole,
} from 'app/KinerjaShared/utils/profile'
import {
  isUserPegawaiPengawasSekolah,
  isUserTimKinerjaPengawasSekolah,
  isUserPPKPengawasSekolah,
} from 'app/KinerjaPSPegawaiPage/utils/profile'
import {
  isUserPenilikPegawai,
  isUserTKPenilik,
  isUserPPKPenilik,
} from 'app/KinerjaPenilikPegawaiPage/utils/profile'
import { NotFoundError } from 'utils/customError'
import useToggleSdkContext from 'utils/useToogleSdk'

interface Props {
  disableLoading?: boolean
  children: React.ReactNode
}

/**
 * Provides content for the Pengawas component.
 *
 * @param disableRedirect - Set to true to disable redirect if user is not pengawas sekolah and you want to handle redirect by yourself. Default is false.
 * @param disableLoading - Set to true to disable loading spinner if you want to handle loading state by yourself. Default is false.
 * @param children - The child components.
 */
export function RoleContentProvider({
  disableLoading = false,
  children,
}: Props) {
  const router = useRouter()
  const toggleValue = useToggleSdkContext()

  const {
    data: { data: profile } = {},
    isLoading: isProfileLoading,
    isFetched: isProfileFetched,
    isError: isProfileError,
  } = useAPIKinerjaProfile({
    options: {
      staleTime: 60 * 60 * 1000, // 1 hour
      cacheTime: 60 * 60 * 1000, // 1 hour
      useErrorBoundary: false,
      retry: false,
    },
  })

  const {
    data: { data: profilePs } = {},
    isLoading: isProfilePsLoading,
    isSuccess: isProfilePsSuccess,
    isFetched: isProfilePsFetched,
    isError: isProfilePsError,
  } = useApiGetPsProfile()

  const {
    data: { data: profilePSPegawai } = {},
    isLoading: isProfilePSPegawaiLoading,
    isSuccess: isProfilePSPegawaiSuccess,
    isFetched: isProfilePSPegawaiFetched,
    isError: isProfilePSPegawaiError,
  } = useAPIGetPSPegawaiProfile({
    enabled: Boolean(toggleValue?.['kinerja-ps-pegawai-enabled']),
  })

  const {
    data: { data: profilePenilikPegawai } = {},
    isLoading: isProfilePenilikPegawaiLoading,
    isSuccess: isProfilePenilikPegawaiSuccess,
    isFetched: isProfilePenilikPegawaiFetched,
    isError: isProfilePenilikPegawaiError,
  } = useAPIGetPenilikPegawaiProfile({
    enabled: Boolean(toggleValue?.['kinerja-penilik-pegawai-enabled']),
  })

  // Considered loading if either profile or profilePs or profilePSPegawai is loading
  const isLoading =
    isProfileLoading ||
    isProfilePsLoading ||
    isProfilePSPegawaiLoading ||
    isProfilePenilikPegawaiLoading

  // Considered success if both profile and profilePs are fetched (even if one of them is error)
  const isSuccess = isProfileFetched && isProfilePsFetched

  // Considered error if all of them is error
  const isError =
    isProfileError &&
    isProfilePsError &&
    isProfilePSPegawaiError &&
    isProfilePenilikPegawaiError

  const isPengawasSekolah =
    (isProfilePsSuccess && isUserPengawasSekolah(profilePs)) || false

  const isKepalaDinas =
    (isProfilePsSuccess && isUserKepalaDinas(profilePs)) || false

  const isKepalaDinasAndPengawasSekolah =
    (isProfilePsSuccess && isUserKepalaDinasAndPengawasSekolah(profilePs)) ||
    false

  // Pegawai Pengawas Sekolah, Tim Kerja, & Pejabat Penilai Kerja
  const isPegawaiPengawasSekolah =
    (isProfilePSPegawaiSuccess &&
      isUserPegawaiPengawasSekolah(profilePSPegawai)) ||
    false

  const isTimKinerjaPengawasSekolah =
    (isProfilePSPegawaiSuccess &&
      isUserTimKinerjaPengawasSekolah(profilePSPegawai)) ||
    false

  const isPPKPengawasSekolah =
    (isProfilePSPegawaiSuccess && isUserPPKPengawasSekolah(profilePSPegawai)) ||
    false

  // SPNF Role Mapping
  const isPamong = isUserHasRole(profile, 'GURU_SPNF')
  const isKepalaSkbPlt = isUserHasRole(profile, 'KS_PLT_SPNF')
  const isKepalaSkbDefinitif = isUserHasRole(profile, 'KS_DEFINITIF_SPNF')

  // Penilik
  const isPenilikPegawai =
    (isProfilePenilikPegawaiSuccess &&
      isUserPenilikPegawai(profilePenilikPegawai)) ||
    false
  const isTKPenilik =
    (isProfilePenilikPegawaiSuccess &&
      isUserTKPenilik(profilePenilikPegawai)) ||
    false
  const isPPKPenilik =
    (isProfilePenilikPegawaiSuccess &&
      isUserPPKPenilik(profilePenilikPegawai)) ||
    false

  // GPK
  const isGpkPegawai =
    (isProfileFetched && isUserHasRole(profile, 'GURU_GPK')) || false
  const isGpkTimKinerja =
    (isProfilePsFetched && isPSUserHasRole(profilePs, 'TK_GPK')) || false

  const leave = () => {
    throw new NotFoundError(router.asPath, new Error('Unauthorized access'))
  }

  if (isLoading && !disableLoading) {
    return (
      <div className="flex flex-col items-center justify-center h-screen gap-4">
        <Spinner data-testid="loading-spinner" />

        {process.env.NODE_ENV === 'development' && (
          <Text>Loading user profile...</Text>
        )}
      </div>
    )
  }

  return (
    <RoleContext.Provider
      value={{
        isLoading,
        isSuccess,
        isError,

        // Guru / Kepala Sekolah
        profile: isSuccess ? profile : null,
        isGuru: isUserHasRole(profile, 'GURU'),
        isKs: isKsDefinitifPlt(profile),
        isKsDefinitif: isUserHasKsRole(profile),
        isKsPlt: isUserHasPltRole(profile),

        // Pamong / Kepala Sekolah SPNF
        isPamong,
        isKepalaSkbPlt,
        isKepalaSkbDefinitif,
        isKepalaSkb: isKepalaSkbPlt || isKepalaSkbDefinitif,

        // Pengawas Sekolah
        profilePs: isSuccess ? profilePs : null,
        isPengawasSekolah,
        isPengawasSekolahOnly: isPengawasSekolah && !isKepalaDinas,

        // Kepala Dinas Pendidikan
        isKepalaDinas,
        isKepalaDinasAndPengawasSekolah,

        // Pegawai Pengawas Sekolah, Tim Kerja, & Pejabat Penilai Kerja
        isPegawaiPengawasSekolah,
        isTimKinerjaPengawasSekolah,
        isPPKPengawasSekolah,
        isProfilePSPegawaiLoading,
        isProfilePSPegawaiFetched,

        isPenilikPegawai,
        isTKPenilik,
        isPPKPenilik,
        isProfilePenilikPegawaiFetched,
        isProfilePenilikPegawaiLoading,

        // GPK
        isGpkPegawai,
        isGpkTimKinerja,

        // Centralize leave function
        leave,
      }}
    >
      {children}
    </RoleContext.Provider>
  )
}
